<script lang="ts">
	//Stores
	import { devices } from 'src/stores/lists/devices';
	import { os } from 'src/stores/session/native-bridge';
	import { getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { cloud_disabled_devices } from 'src/stores/device-runtime/firmware-ui';
	import { pin_unlocked_devices } from 'src/stores/device-runtime/pin-unlocked';

	//Helpers
	import { hapticFeedback } from '../../../services/native_container_requests_ios';
	import {
		get_gen_2_component_channel_structure,
		hasTilt,
		real_id,
	} from '../../../helpers/interface_helpers';
	import { unlockPin } from '../../../helpers/pin_lock_helpers';

	// might have to pass button component as a prop
	// import DoubleIconedButtonIndication from "./ControlButton/DoubleIconedButtonIndication.svelte";
	// import DoubleButton from './ControlButton/DoubleButton.svelte';
	import ControlsContainer from '@Shelly/shelly-design-system/ControlsContainer/ControlsContainer.svelte';
	import DeviceButtonV2 from '@Shelly/shelly-design-system/DeviceButtonV2/DeviceButtonV2.svelte';
	import Text from '@Shelly/shelly-design-system/Text/Text.svelte';
	import PinUnlock from '../../PinUnlock.svelte';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { format } from 'svelte-i18n';
	import { toast } from '@Shelly/shelly-design-system';
	import { roller_is_calibrated } from 'src/helpers/requests/calibrate_cover';
	import { hasControlRights } from 'src/helpers/user_helpers';

	$: settingsStore = getDeviceSettings(device.id);
	export let device, status;

	let isLoadingOpen = false;
	let isLoadingClose = false;
	let isLoadingBlinds = false;
	let direction = 'stop';
	let hasSlat = false;

	let showModal = false;

	let callbackValue;

	let btn1 = false;
	let btn2 = false;

	$: hasRights = hasControlRights(device);

	$: pinLocked =
		typeof $pin_unlocked_devices[device.id] === 'undefined' && $devices[device.id]?.pin_lock;

	$: rollerCalibrated = roller_is_calibrated(device, status, $settingsStore);
	$: rollerLostPosition =
		status?.[`${get_gen_2_component_channel_structure(device)}`]?.current_pos === null;
	$: {
		direction = status?.['cover:' + device.channel]?.state;
		btn1 = status?.['cover:' + device.channel]?.state === 'opening';
		btn2 = status?.['cover:' + device.channel]?.state === 'closing';
		hasSlat =
			hasTilt(device) &&
			$settingsStore?.[get_gen_2_component_channel_structure(device)]?.slat?.enable &&
			rollerCalibrated &&
			!rollerLostPosition; //using to hide open blinds button
	}

	const handleRequestGen2 = async (dir) => {
		if ((dir === 1 && direction === 'opening') || (dir === 0 && direction === 'closing')) {
			direction = 'stopped';
			if ($cloud_disabled_devices[device.id]) {
				btn1 = false;
				btn2 = false;
			}
		} else {
			direction = dir === 1 ? 'opening' : 'closing';
			if ($cloud_disabled_devices[device.id]) {
				btn1 = direction === 'opening';
				btn2 = direction === 'closing';
			}
		}

		// TODo: ask SPAS for a better endpoint /device/${device.category}/control
		const method =
			direction === 'opening'
				? 'cover.open'
				: direction === 'closing'
					? 'cover.close'
					: 'cover.stop';

		const res = await gen2Request(device, method, {
			id: device.channel,
		});

		if (res.isok) {
			status['cover:' + device.channel] = {
				res,
				...status['cover:' + device.channel],
			};

			devices_status.update_device_status({
				id: real_id(device.id),
				msg: status,
			});
		} else {
			console.error('Device control failed');
		}
	};

	const handleRequest = async (dir) => {
		if (!pinLocked) {
			if ($os === 'IOS') {
				hapticFeedback('buttonPressed');
			}

			if (dir) {
				isLoadingOpen = true;
			} else {
				isLoadingClose = true;
			}

			await handleRequestGen2(dir);

			if (dir) {
				isLoadingOpen = false;
			} else {
				isLoadingClose = false;
			}
		} else {
			callbackValue = dir;
			showModal = true;
		}
	};

	const handlePin = () => {
		pinLocked = unlockPin(device, pinLocked);
		if (callbackValue === 'blinds') {
			openBlinds();
		} else {
			handleRequest(callbackValue);
		}
		callbackValue = undefined;
	};

	const openBlinds = async () => {
		if (!pinLocked) {
			isLoadingBlinds = true;
			const res = await gen2Request(device, 'Cover.GoToPosition', {
				id: device.channel,
				['slat_pos']: 100,
			});

			if (res.isok) {
			} else {
				toast.open({ type: 'danger', title: $format('error_global') });
			}
			isLoadingBlinds = false;
		} else {
			callbackValue = 'blinds';
			showModal = true;
		}
	};
</script>

{#if hasSlat}
	<!-- TODO here need to add min version check for gen 2 ++ devices but unfortunately fw is not available yet. Until then we are using device type comparison. 23072024 Pako -->
	<ControlsContainer color="color-0">
		<svelte:fragment slot="first">
			<DeviceButtonV2
				id="DeviceButtonV2-gsj5cnj"
				icon={btn1 ? 'fa fa-pause' : 'fa fa-blinds-raised'}
				isActive={btn1}
				aria-pressed={btn1}
				aria-label={$format('label_direction_up_or_pause', { default: 'Direction Up or Pause' })}
				class="text-bold two-lines-ellipsis controls-small-styles"
				isLoading={isLoadingOpen}
				on:click={(e) => {
					e.stopPropagation();
					handleRequest(1);
				}}
				disabled={!hasRights}
			/>
			<Text styleType="body-small" class="mt-s-plus text-bold">
				{$format('text_device_control_direction_open_up', {
					default: 'Open up',
				})}
			</Text>
		</svelte:fragment>

		<svelte:fragment slot="second">
			<DeviceButtonV2
				aria-label={$format('label_direction_fully_open', { default: 'Direction Fully Open' })}
				id="DeviceButtonV2-awr9h88"
				icon={'fa fa-blinds-open'}
				isActive={isLoadingBlinds}
				class="text-bold two-lines-ellipsis controls-small-styles"
				on:click={(e) => {
					e.stopPropagation();
					openBlinds();
				}}
				disabled={!hasRights}
			/>
			<Text styleType="body-small" class="mt-s-plus text-bold">
				{$format('text_device_control_direction_fully_open', {
					default: 'Fully open',
				})}
			</Text>
		</svelte:fragment>

		<svelte:fragment slot="third">
			<DeviceButtonV2
				id="DeviceButtonV2-v19d3zj"
				icon={btn2 ? 'fa fa-pause' : 'fa fa-blinds'}
				isActive={btn2}
				aria-pressed={btn2}
				aria-label={$format('label_direction_down_or_pause', {
					default: 'Direction Down or Pause',
				})}
				class="text-bold two-lines-ellipsis controls-small-styles"
				isLoading={isLoadingClose}
				on:click={(e) => {
					e.stopPropagation();
					handleRequest(0);
				}}
				disabled={!hasRights}
			/>
			<Text styleType="body-small" class="mt-s-plus text-bold">
				{$format('text_device_control_direction_close_down', {
					default: 'Close down',
				})}
			</Text>
		</svelte:fragment>
	</ControlsContainer>
{:else}
	<ControlsContainer color="color-0">
		<svelte:fragment slot="first">
			<DeviceButtonV2
				id="DeviceButtonV2-0ujbydk"
				icon={btn1 ? 'fa fa-pause' : 'fa fa-arrow-up'}
				isActive={btn1}
				aria-pressed={btn1}
				aria-label={$format('label_direction_up_or_pause', { default: 'Direction Up or Pause' })}
				size="l"
				isLoading={isLoadingOpen}
				on:click={(e) => {
					e.stopPropagation();
					handleRequest(1);
				}}
				disabled={!hasRights}
			/>
			<Text styleType="controls-small" class="mt-s-plus text-bold">
				{$format('text_device_control_direction_open', {
					default: 'Open',
				})}
			</Text>
		</svelte:fragment>
		<svelte:fragment slot="second">
			<DeviceButtonV2
				id="DeviceButtonV2-xf706pz"
				icon={btn2 ? 'fa fa-pause' : 'fa fa-arrow-down'}
				isActive={btn2}
				aria-pressed={btn2}
				aria-label={$format('label_direction_down_or_pause', {
					default: 'Direction Down or Pause',
				})}
				size="l"
				isLoading={isLoadingClose}
				on:click={(e) => {
					e.stopPropagation();
					handleRequest(0);
				}}
				disabled={!hasRights}
			/>
			<Text styleType="controls-small" class="mt-s-plus text-bold">
				{$format('text_device_control_direction_close', {
					default: 'Close',
				})}
			</Text>
		</svelte:fragment>
	</ControlsContainer>
{/if}

{#if showModal}
	<PinUnlock bind:showModal on:unlock={handlePin} on:cancel={() => (showModal = false)} />
{/if}
